/**
 * Firebase Phone Auth — sends OTP via Firebase instead of Twilio, then
 * returns the Firebase ID token for backend verification.
 *
 * Flow:
 *   1. sendOtp(phoneNumber)  → sets up RecaptchaVerifier + signInWithPhoneNumber
 *   2. verifyOtp(code)       → confirms the code, returns the ID token
 *   3. getIdToken()          → re-fetches the current user's ID token
 *
 * The backend accepts this ID token as an alternative to the Twilio OTP flow.
 *
 * reCAPTCHA note: Google's api.js registers rendered widgets PER DOM ELEMENT,
 * and that registry survives `verifier.clear()` / wiping innerHTML. Rendering
 * a second verifier into the same element throws "reCAPTCHA has already been
 * rendered in this element" (seen on Resend OTP / back-and-resubmit). So each
 * verifier gets a FRESH child element inside the stable
 * `#recaptcha-container` host that the login/signup routes render; the used
 * child is removed on reset and never reused.
 */
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type ConfirmationResult,
} from 'firebase/auth';
import { firebaseAuth } from './firebase';

// Local development only: skip reCAPTCHA so Firebase "phone numbers for
// testing" (Firebase console → Authentication → Sign-in method → Phone) work
// without SMS. Firebase only honours the fake token for allow-listed test
// numbers, and this block is dead code in production builds.
if (import.meta.env.DEV && import.meta.env.VITE_FIREBASE_APP_VERIFICATION_DISABLED === 'true') {
  firebaseAuth.settings.appVerificationDisabledForTesting = true;
}

let confirmationResult: ConfirmationResult | null = null;
let recaptchaVerifier: RecaptchaVerifier | null = null;
/** Disposable element the current verifier rendered into (child of the host). */
let widgetHost: HTMLElement | null = null;
let isSending = false;
let isVerifying = false;

const log = (...args: unknown[]) => {
  if (import.meta.env.DEV) console.info('[firebase-phone-auth]', ...args);
};

/**
 * Map raw Firebase/reCAPTCHA failures to copy safe to show users. Anything we
 * don't recognise is rethrown untouched so genuinely useful messages survive.
 */
function toFriendlyError(err: unknown): Error {
  const code = (err as { code?: string } | null)?.code ?? '';
  const message = err instanceof Error ? err.message : '';

  switch (code) {
    case 'auth/too-many-requests':
      return new Error('Too many attempts — please wait a few minutes and try again.');
    case 'auth/invalid-phone-number':
    case 'auth/missing-phone-number':
      return new Error("That mobile number doesn't look right. Please check and try again.");
    case 'auth/network-request-failed':
      return new Error('Network error — please check your connection and try again.');
    case 'auth/quota-exceeded':
      return new Error('SMS limit reached — please try again in a little while.');
  }
  // Belt-and-braces: never surface reCAPTCHA internals to the user.
  if (/recaptcha/i.test(message)) {
    return new Error('Could not start verification — please try again.');
  }
  return err instanceof Error ? err : new Error('Failed to send OTP. Please try again.');
}

function resetVerifier() {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch {
      // The container may already be gone (route change unmounted it) —
      // clear() throws in that case and there is nothing left to clean.
    }
    recaptchaVerifier = null;
  }
  // Remove the used element entirely. grecaptcha tracks rendered widgets per
  // element, so a wiped-but-reused element would throw "already been
  // rendered" on the next render — a fresh node sidesteps that registry.
  if (widgetHost) {
    widgetHost.remove();
    widgetHost = null;
  }
}

/**
 * Send an OTP to the given phone number via Firebase Phone Auth.
 * Requires a DOM element with id="recaptcha-container" to be mounted.
 */
export async function sendFirebaseOtp(phoneNumber: string): Promise<void> {
  if (isSending) {
    log('sendFirebaseOtp SKIPPED — already in progress');
    return;
  }
  isSending = true;
  log('sendFirebaseOtp START', phoneNumber);
  try {
    confirmationResult = null;
    resetVerifier();

    const container = document.getElementById('recaptcha-container');
    if (!container) {
      log('sendFirebaseOtp FAILED — #recaptcha-container not mounted');
      throw new Error('Could not start verification — please reload the page and try again.');
    }

    // Fresh, never-before-rendered element for this verifier (see module doc).
    widgetHost = document.createElement('div');
    container.appendChild(widgetHost);

    recaptchaVerifier = new RecaptchaVerifier(firebaseAuth, widgetHost, {
      size: 'invisible',
    });

    confirmationResult = await signInWithPhoneNumber(
      firebaseAuth,
      phoneNumber,
      recaptchaVerifier,
    );
    log('sendFirebaseOtp OK — confirmationResult stored');
  } catch (err) {
    log('sendFirebaseOtp FAILED', err);
    throw toFriendlyError(err);
  } finally {
    isSending = false;
  }
}

/**
 * Verify the OTP code entered by the user and return the Firebase ID token.
 */
export async function verifyFirebaseOtp(code: string): Promise<string> {
  if (isVerifying) {
    log('verifyFirebaseOtp SKIPPED — already in progress');
    throw new Error('Verification already in progress.');
  }
  if (!confirmationResult) {
    log('verifyFirebaseOtp FAILED — no confirmationResult');
    throw new Error('No pending OTP verification. Call sendFirebaseOtp first.');
  }

  isVerifying = true;
  log('verifyFirebaseOtp START');
  try {
    const credential = await confirmationResult.confirm(code);
    const idToken = await credential.user.getIdToken();
    log('verifyFirebaseOtp OK — got idToken');
    confirmationResult = null;
    return idToken;
  } catch (err) {
    log('verifyFirebaseOtp FAILED', err);
    throw err;
  } finally {
    isVerifying = false;
  }
}
